import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from '../hooks/use-toast';
import { Gavel, Plus, Search, Pencil, Trash2, CheckCircle2, Ban, History, ShieldAlert } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const LEVEL_COLORS = {
  verbal: 'bg-yellow-100 text-yellow-700 border-yellow-300',
  written: 'bg-orange-100 text-orange-700 border-orange-300',
  final_written: 'bg-red-100 text-red-700 border-red-300',
  suspension: 'bg-purple-100 text-purple-700 border-purple-300',
  termination: 'bg-gray-800 text-white border-gray-800',
};
const STATUS_COLORS = {
  active: 'bg-rose-100 text-rose-700',
  resolved: 'bg-emerald-100 text-emerald-700',
  revoked: 'bg-gray-200 text-gray-600',
};

const fmt = (d) => { if (!d) return '—'; try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return d; } };

const RANK = { verbal: 1, written: 2, final_written: 3, suspension: 4, termination: 5 };
const NEXT_SUGGEST = { verbal: 'Written Warning', written: 'Final Written Warning', final_written: 'Suspension', suspension: 'Termination' };
const LEVEL_LABEL_SHORT = { verbal: 'Verbal Warning', written: 'Written Warning', final_written: 'Final Written Warning', suspension: 'Suspension', termination: 'Termination' };

const emptyForm = { employee_id: '', category: '', level: '', incident_date: '', description: '', action_taken: '', warning_letter_ref: '' };

const Discipline = () => {
  const [records, setRecords] = useState([]);
  const [meta, setMeta] = useState({ categories: [], levels: [], statuses: [] });
  const [stats, setStats] = useState({ active: 0, total: 0, by_level: {} });
  const [employees, setEmployees] = useState([]);
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [fCategory, setFCategory] = useState('all');
  const [fLevel, setFLevel] = useState('all');
  const [fStatus, setFStatus] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [historyRec, setHistoryRec] = useState(null);

  const levelLabel = useCallback((k) => (meta.levels.find(l => l.key === k)?.label || k), [meta.levels]);
  const userName = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}').name || 'HR/Admin'; } catch { return 'HR/Admin'; } })();

  const load = useCallback(async () => {
    try {
      const [r, s] = await Promise.all([
        axios.get(`${API}/api/discipline`),
        axios.get(`${API}/api/discipline/stats`),
      ]);
      setRecords(Array.isArray(r.data) ? r.data : []);
      setStats(s.data || {});
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    axios.get(`${API}/api/discipline/meta`).then(r => setMeta(r.data || {})).catch(() => {});
    axios.get(`${API}/api/employees`).then(r => setEmployees(r.data || [])).catch(() => {});
    load();
  }, [load]);

  const filtered = useMemo(() => records.filter(r => {
    if (fCategory !== 'all' && r.category !== fCategory) return false;
    if (fLevel !== 'all' && r.level !== fLevel) return false;
    if (fStatus !== 'all' && r.status !== fStatus) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!(`${r.employee_name} ${r.employee_code} ${r.category} ${r.description}`.toLowerCase().includes(q))) return false;
    }
    return true;
  }), [records, fCategory, fLevel, fStatus, search]);

  // Escalation hints: 2+ active cases of the same level → suggest next step.
  const escalations = useMemo(() => {
    const byEmp = {};
    records.filter(r => r.status === 'active').forEach(r => {
      byEmp[r.employee_id] = byEmp[r.employee_id] || { id: r.employee_id, name: r.employee_name, code: r.employee_code, counts: {} };
      byEmp[r.employee_id].counts[r.level] = (byEmp[r.employee_id].counts[r.level] || 0) + 1;
    });
    const out = [];
    Object.values(byEmp).forEach(e => {
      const lvl = Object.keys(e.counts).sort((a, b) => (RANK[b] || 0) - (RANK[a] || 0)).find(l => NEXT_SUGGEST[l] && e.counts[l] >= 2);
      if (lvl) out.push({ ...e, level: lvl, count: e.counts[lvl], suggest: NEXT_SUGGEST[lvl] });
    });
    return out;
  }, [records]);
  const formEsc = escalations.find(e => e.id === form.employee_id);

  const openNew = () => { setEditing(null); setForm(emptyForm); setShowForm(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ employee_id: r.employee_id, category: r.category, level: r.level, incident_date: (r.incident_date || '').slice(0, 10), description: r.description, action_taken: r.action_taken || '', warning_letter_ref: r.warning_letter_ref || '' });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.employee_id || !form.category || !form.level || !form.description.trim()) {
      toast({ title: 'Missing fields', description: 'Employee, category, level and description are required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/api/discipline/${editing.id}`, { category: form.category, level: form.level, incident_date: form.incident_date, description: form.description, action_taken: form.action_taken, warning_letter_ref: form.warning_letter_ref });
      } else {
        await axios.post(`${API}/api/discipline`, { ...form, issued_by: userName });
      }
      toast({ title: editing ? 'Record updated' : 'Disciplinary record added' });
      setShowForm(false); load();
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || 'Failed to save', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const setStatus = async (r, status) => {
    let note = '';
    if (status === 'revoked') { note = window.prompt('Reason for revoking this record?') || ''; }
    try {
      await axios.patch(`${API}/api/discipline/${r.id}/status`, { status, note, actioned_by: userName });
      toast({ title: `Marked ${status}`, description: `${r.employee_name} — ${levelLabel(r.level)}` });
      load();
    } catch { toast({ title: 'Error', description: 'Could not update status', variant: 'destructive' }); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete this disciplinary record for ${r.employee_name}? This cannot be undone.`)) return;
    try { await axios.delete(`${API}/api/discipline/${r.id}`); toast({ title: 'Deleted' }); load(); }
    catch { toast({ title: 'Error', description: 'Could not delete', variant: 'destructive' }); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="discipline-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-rose-100 flex items-center justify-center">
            <Gavel className="h-6 w-6 text-rose-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Employee Discipline</h1>
            <p className="text-sm text-gray-500">Track warnings, categories and the full disciplinary history</p>
          </div>
        </div>
        <Button onClick={openNew} data-testid="add-discipline-btn"><Plus className="h-4 w-4 mr-2" /> New Record</Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Card className="border-0 shadow-sm"><CardContent className="p-4">
          <p className="text-[10px] uppercase text-gray-400 tracking-wider">Active Cases</p>
          <p className="text-2xl font-bold text-rose-600" data-testid="stat-active">{stats.active || 0}</p>
        </CardContent></Card>
        {(meta.levels || []).map(l => (
          <Card key={l.key} className="border-0 shadow-sm"><CardContent className="p-4">
            <p className="text-[10px] uppercase text-gray-400 tracking-wider truncate">{l.label}</p>
            <p className="text-2xl font-bold text-gray-800">{stats.by_level?.[l.key] || 0}</p>
          </CardContent></Card>
        ))}
      </div>

      {/* Escalation hints */}
      {escalations.length > 0 && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4" data-testid="escalation-banner">
          <p className="text-sm font-semibold text-amber-800 flex items-center gap-2">
            <ShieldAlert className="h-4 w-4" /> Escalation review suggested
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {escalations.map(e => (
              <button
                key={e.id}
                onClick={() => setSearch(e.code)}
                className="text-xs bg-white border border-amber-300 rounded-full px-3 py-1 hover:bg-amber-100"
                data-testid={`escalation-${e.id}`}
              >
                <b>{e.name}</b> · {e.count} active {LEVEL_LABEL_SHORT[e.level] || e.level} → consider <b>{e.suggest}</b>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input className="pl-9" placeholder="Search employee, category, description…" value={search} onChange={e => setSearch(e.target.value)} data-testid="discipline-search" />
        </div>
        <Select value={fCategory} onValueChange={setFCategory}><SelectTrigger className="w-48" data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Categories</SelectItem>{(meta.categories || []).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
        <Select value={fLevel} onValueChange={setFLevel}><SelectTrigger className="w-44" data-testid="filter-level"><SelectValue placeholder="Level" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Levels</SelectItem>{(meta.levels || []).map(l => <SelectItem key={l.key} value={l.key}>{l.label}</SelectItem>)}</SelectContent></Select>
        <Select value={fStatus} onValueChange={setFStatus}><SelectTrigger className="w-36" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Status</SelectItem><SelectItem value="active">Active</SelectItem><SelectItem value="resolved">Resolved</SelectItem><SelectItem value="revoked">Revoked</SelectItem></SelectContent></Select>
      </div>

      {/* List */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-0 divide-y">
          {filtered.length === 0 ? (
            <div className="py-16 text-center text-gray-400 flex flex-col items-center gap-2">
              <ShieldAlert className="h-8 w-8" /> No disciplinary records
            </div>
          ) : filtered.map(r => (
            <div key={r.id} className="p-4 flex flex-wrap items-start gap-3 hover:bg-gray-50" data-testid={`discipline-row-${r.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-gray-900">{r.employee_name}</span>
                  <span className="text-xs text-gray-400">{r.employee_code} · {r.department || '—'}</span>
                  <Badge variant="outline" className={LEVEL_COLORS[r.level] || ''} data-testid={`level-${r.id}`}>{levelLabel(r.level)}</Badge>
                  <Badge className={STATUS_COLORS[r.status] || ''}>{r.status}</Badge>
                </div>
                <p className="text-xs text-violet-600 font-medium mt-1">{r.category}</p>
                <p className="text-sm text-gray-600 mt-0.5">{r.description}</p>
                {r.action_taken ? <p className="text-xs text-gray-500 mt-1"><b>Action:</b> {r.action_taken}</p> : null}
                <p className="text-[11px] text-gray-400 mt-1">Incident {fmt(r.incident_date)} · Issued by {r.issued_by}{r.status_note ? ` · Note: ${r.status_note}` : ''}</p>
              </div>
              <div className="flex items-center gap-1">
                <Button size="icon" variant="ghost" title="History" onClick={() => setHistoryRec(r)} data-testid={`history-${r.id}`}><History className="h-4 w-4 text-gray-500" /></Button>
                {r.status === 'active' && (
                  <>
                    <Button size="icon" variant="ghost" title="Mark resolved" onClick={() => setStatus(r, 'resolved')} data-testid={`resolve-${r.id}`}><CheckCircle2 className="h-4 w-4 text-emerald-600" /></Button>
                    <Button size="icon" variant="ghost" title="Revoke" onClick={() => setStatus(r, 'revoked')} data-testid={`revoke-${r.id}`}><Ban className="h-4 w-4 text-gray-500" /></Button>
                  </>
                )}
                <Button size="icon" variant="ghost" title="Edit" onClick={() => openEdit(r)} data-testid={`edit-${r.id}`}><Pencil className="h-4 w-4 text-sky-600" /></Button>
                <Button size="icon" variant="ghost" title="Delete" onClick={() => remove(r)} data-testid={`delete-${r.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Add / Edit dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="discipline-form">
          <DialogHeader><DialogTitle>{editing ? 'Edit Disciplinary Record' : 'New Disciplinary Record'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-gray-500">Employee</label>
              <Select value={form.employee_id} onValueChange={v => setForm(f => ({ ...f, employee_id: v }))} disabled={!!editing}>
                <SelectTrigger data-testid="form-employee"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.employee_code} · {e.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {formEsc && (
              <div className="flex items-start gap-2 text-xs bg-amber-50 border border-amber-200 rounded-lg p-2 text-amber-800" data-testid="form-escalation-hint">
                <ShieldAlert className="h-4 w-4 flex-shrink-0 mt-0.5" />
                This employee already has {formEsc.count} active {LEVEL_LABEL_SHORT[formEsc.level] || formEsc.level}(s). Consider issuing a <b>{formEsc.suggest}</b>.
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-500">Category</label>
                <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                  <SelectTrigger data-testid="form-category"><SelectValue placeholder="Category" /></SelectTrigger>
                  <SelectContent>{(meta.categories || []).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500">Warning Level</label>
                <Select value={form.level} onValueChange={v => setForm(f => ({ ...f, level: v }))}>
                  <SelectTrigger data-testid="form-level"><SelectValue placeholder="Level" /></SelectTrigger>
                  <SelectContent>{(meta.levels || []).map(l => <SelectItem key={l.key} value={l.key}>{l.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Incident Date</label>
              <Input type="date" value={form.incident_date} onChange={e => setForm(f => ({ ...f, incident_date: e.target.value }))} data-testid="form-date" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Description</label>
              <Textarea rows={3} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="What happened?" data-testid="form-description" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Action Taken (optional)</label>
              <Textarea rows={2} value={form.action_taken} onChange={e => setForm(f => ({ ...f, action_taken: e.target.value }))} placeholder="Corrective action / next steps" data-testid="form-action" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Warning Letter Ref (optional)</label>
              <Input value={form.warning_letter_ref} onChange={e => setForm(f => ({ ...f, warning_letter_ref: e.target.value }))} placeholder="e.g. WL-2026-014" data-testid="form-ref" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-discipline-btn">{saving ? 'Saving…' : (editing ? 'Update' : 'Add Record')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History dialog */}
      <Dialog open={!!historyRec} onOpenChange={(o) => !o && setHistoryRec(null)}>
        <DialogContent className="max-w-md" data-testid="history-dialog">
          <DialogHeader><DialogTitle>History — {historyRec?.employee_name}</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {(historyRec?.history || []).map((h, i) => (
              <div key={i} className="flex items-start gap-2 text-sm">
                <div className="h-2 w-2 rounded-full bg-violet-400 mt-1.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-800 capitalize">{h.event}{h.note ? ` — ${h.note}` : ''}</p>
                  <p className="text-[11px] text-gray-400">{fmt(h.at)} · {h.by}</p>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Discipline;
